import { AppPrograms } from '@gamiphy/bl/app';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { CoreStore } from '@gamiphy/loyalty-station-desktop/store/core';
import { ProductStore } from '@gamiphy/loyalty-station-desktop/store/products';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { isMobile } from '@gamiphy/utils/device';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@gamiphy/utils/storage';
import {
  action, computed, makeAutoObservable, observable, observe, when,
} from 'mobx';
import { AppWelcomeGiftStore } from '../appWelcomeGift';
import { DiscountsStore } from '../discounts';
import { UserStore } from '../user';
import {
  UI_LOCAL_STORAGE, URL_PARAMS,
} from './constants';
import {
  Pages,
  Tabs,
} from './enums';
import { addRouteChangeListener, formatTab } from './utils';
import { SessionStore } from '../session';

class UiStore {
  @observable initialized = false;

  @observable opened = false;

  @observable fullBar = false;

  @observable widgetVisible = true;

  @observable barHidden = false;

  @observable loadingProductPage = false;

  @observable floatingWidgetVisible = false;

  @observable floatingLiveVisible = false;

  @observable showGame = false;

  @observable signupPopupOpen = false;

  @observable welcomeGiftNotificationOpen = false;

  @observable checkoutExampleOpen = false;

  @observable loyaltyPageOpen = false;

  @observable
  private selectedPage?: Pages = undefined;

  @observable
  private selectedTab?: string = undefined;

  @observable signupFlowStarted = false;

  private readonly urlParams = new URLSearchParams(window.location.search);

  constructor() {
    makeAutoObservable(this);

    void this.watchDefaultValues();

    when(() => UserStore.isRealUser, () => {
      const isAutoOpen = getLocalStorageItem(UI_LOCAL_STORAGE.automaticallyOpen);
      if (isAutoOpen) {
        this.open();
        removeLocalStorageItem(UI_LOCAL_STORAGE.automaticallyOpen);
      }
    });

    when(() => AppStore.initialized && UserStore.isLoggedIn, () => {
      void (async () => {
        const code = this.urlParams.get('code');
        const source = this.urlParams.get('source');
        if (code && source) {
          await UserStore.socialConnect({
            type: source,
            code,
            redirectUri: 'https://static.gamiphy.co/fe/utils/natv2/index.html',
          });

          // Remove code and source from the url

          this.urlParams.delete('code');
          this.urlParams.delete('source');

          const newUrl = new URL(window.location.toString());
          newUrl.search = this.urlParams.toString();

          window.history.pushState({}, document.title, newUrl.toString());
        }
      })();
    });

    observe(this, 'showWelcomeGiftNotification', () => {
      if (this.showWelcomeGiftNotification) {
        SessionStore.updateSession({ welcomeGiftNotification: { opened: true } });
      }
    });

    void this.autoOpenWelcomeGiftNotification();

    when(() => CoreStore.initialized, () => {
      if (!CoreStore.visible) {
        this.hide();
      }
      const checkVisibility = () => {
        if (this.widgetVisible !== ThemeStore.isVisible) {
          if (this.widgetVisible) {
            this.hide();
          } else {
            this.show();
          }
        }
      };
      checkVisibility();
      addRouteChangeListener(checkVisibility);

      this.initialized = true;
    });
  }

  @computed get currentPage(): Pages {
    switch (true) {
      case !AppStore.isCashbackProgram && !UserStore.isLoggedIn:
        return Pages.landing;
      default:
        return this.selectedPage ?? Pages.home;
    }
  }

  @computed get currentTab(): string | undefined {
    return this.selectedTab;
  }

  @computed get showWelcomeGiftNotification(): boolean {
    return !this.opened && this.welcomeGiftNotificationOpen &&
      !!AppWelcomeGiftStore.welcomeGift?.enabled && !DiscountsStore.welcomeGiftDiscount;
  }

  @action open(): void {
    this.opened = true;
    this.showFullBar();
  }

  @action autoOpen(): void {
    setLocalStorageItem(UI_LOCAL_STORAGE.automaticallyOpen, 'true');
  }

  @action showFullBar(): void {
    this.fullBar = true;
  }

  @action close(): void {
    this.opened = false;
  }

  @action toggle(): void {
    this.opened = !this.opened;
  }

  @action hide(): void {
    this.widgetVisible = false;
  }

  @action show(force?: boolean): void {
    if (force || ThemeStore.isVisible) {
      this.widgetVisible = true;
    }
  }

  @action hideBar(): void {
    this.barHidden = true;
  }

  @action showBar(): void {
    this.barHidden = false;
  }

  @action openSignupPopup(): void {
    this.signupPopupOpen = true;
  }

  @action closeSignupPopup(): void {
    this.signupPopupOpen = false;
  }

  @action async autoOpenWelcomeGiftNotification(): Promise<void> {
    await when(() => ThemeStore.initialized);

    const mobile = isMobile();

    if ((!mobile && !ThemeStore.theme.notification.disabled.desktop)) {
      this.openWelcomeGiftNotification();
    } else if (mobile && !ThemeStore.theme.notification.disabled.mobile) {
      this.openWelcomeGiftNotification();
    }
  }

  @action openWelcomeGiftNotification(): void {
    if (!SessionStore.coretavaSession?.welcomeGiftNotification?.opened) {
      this.welcomeGiftNotificationOpen = true;
    }
  }

  @action closeWelcomeGiftNotification(): void {
    this.welcomeGiftNotificationOpen = false;
  }

  @action toggleFloatingWidget(): void {
    this.floatingWidgetVisible = !this.floatingWidgetVisible;
  }

  @action toggleFloatingLive(): void {
    this.floatingLiveVisible = !this.floatingLiveVisible;
  }

  @action setLoadingOfProductPage(loading: boolean): void {
    this.loadingProductPage = loading;
  }

  @action setRedeemPageContent(): void {
    if (this.loadingProductPage) {
      this.setLoadingOfProductPage(false);
      window.open(ProductStore.viewedProduct?.url ? ProductStore.viewedProduct.url : '');
    } else {
      this.setPage(Pages.redeem);
    }
  }

  @action setPage(page: Pages) {
    this.selectedPage = page;
  }

  @action setTab(tab?: Tabs, id?: string) {
    if (!tab) {
      this.selectedTab = undefined;
    } else {
      this.selectedTab = formatTab(tab, id);
    }
  }

  @action closePage() {
    this.selectedPage = undefined;
  }

  @action setShowGame(showGame: boolean) {
    this.showGame = showGame;
  }

  @action setLoyaltyPageOpen(open: boolean): void {
    this.loyaltyPageOpen = open;
  }

  @action openCheckoutExample(): void {
    this.checkoutExampleOpen = true;
  }

  @action closeCheckoutExample(): void {
    this.checkoutExampleOpen = false;
  }

  @action async watchDefaultValues(): Promise<void> {
    // Wait for the UI store to be fully initialized
    await when(() => this.initialized);
    // Wait for the LS to be visible
    await when(() => this.widgetVisible);
    // Wait for the theme store to initialize
    await when(() => ThemeStore.initialized);

    // Check for auto open
    if (this.urlParams.get(URL_PARAMS.gamiphyOpen)) {
      this.open();
    } else if (ThemeStore.theme.widget.autoOpened?.mobile && isMobile()) {
      this.open();
    } else if (ThemeStore.theme.widget.autoOpened?.desktop && !isMobile()) {
      this.open();
    }

    // Check for default tabs
    const defaultTab = this.urlParams.get(URL_PARAMS.gamiphyTab) as Tabs;
    if (!defaultTab && AppStore.app.program === AppPrograms.cashback) {
      this.selectedTab = Tabs.WelcomeGift;
    } else if (defaultTab && Object.values(Tabs)
      .includes(defaultTab)) {
      if (defaultTab === Tabs.WelcomeGift) {
        this.setPage(Pages.survey);
      } else {
        this.setPage(Pages.home);
      }
      this.setTab(defaultTab);
      this.setPage(Pages.home);
    } else {
      this.setTab(Tabs.WelcomeGift);
    }
  }

  @action
  setSignupFlowStarted(value: boolean): void {
    this.signupFlowStarted = value;
  }
}

export default new UiStore();
